import React, { useState, useEffect } from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import FlexGroup from 'components/FlexGroup';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import Pagination, { Refresh, TotalCount } from 'components/Pagination';
import PageSizeSelect from 'components/Pagination/PageSizeSelect';
import TotalSelection from 'components/TotalSelection';
import AuthFeature from 'components/AuthFeature';
import { Translate } from 'react-redux-i18n';
import { AUTH_EDIT_FILTER } from 'constants/permission';
import permissionHandler from 'helpers/permission-handler';
import { NEW_FILTER } from 'constants/routes';
import ListView from 'components/ListView';
import mobile from 'is-mobile';
import ListRow from './ListRow';
import headerConfig from './ListHeader';
import RemoveFilterModel from './RemoveFilterModal';

const isMobile = mobile();

const ListRowGroup = ({ columns, rowData }) => (
    <ListRow
        key={ `filterList-${ rowData.id }` }
        columns={ columns }
        data={ rowData }
    />
);

ListRowGroup.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    rowData: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        fleet_user_id: PropTypes.string,
        type: PropTypes.number,
        pin: PropTypes.bool,
        criteria: PropTypes.string,
        description: PropTypes.string,
        update_time: PropTypes.string,
        has_permission: PropTypes.bool,
    }).isRequired,
};

const FilterManagementList = ({
    onChange,
    onFetch,
    onSort,
    onSelect,
    data,
    selectedIdList,
    onRowSelected,
    onExport,
}) => {
    const [showRemove, setShowRemove] = useState(false);
    const [responseTime, setResponseTime] = useState(Date.now());
    const {
        data_list,
        page_count,
        page_index,
        size: pageSize,
        total,
        updatedTime,
    } = data;

    const handleRemove = e => {
        setShowRemove(true);
    };

    const closeRemoveModal = e => {
        setShowRemove(false);
    };

    useEffect(() => {
        if (updatedTime > responseTime) {
            setResponseTime(updatedTime);
        }
    }, [responseTime, updatedTime]);

    return (
        <>
            <section className="list-view-wrap">
                <FlexGroup spaceBetween className="pagination-control-sec">
                    <FlexGroup>
                        <FlexGroup className="list-action-sec">
                            <AuthFeature requiredList={ [AUTH_EDIT_FILTER] }>
                                <NavLink to={ NEW_FILTER }>
                                    <Button
                                        className="new-btn"
                                        outline
                                        type="button"
                                    >
                                        <Translate value="filters.new" />
                                    </Button>
                                </NavLink>
                                <Button
                                    type="button"
                                    outline
                                    onClick={ onExport }
                                    disabled={ !selectedIdList.length }>
                                    <Translate value="export" />
                                </Button>
                                <Button
                                    className="remove-btn"
                                    outline
                                    type="button"
                                    disabled={ !selectedIdList.length }
                                    onClick={ handleRemove }
                                >
                                    <Translate value="filters.remove" />
                                </Button>
                            </AuthFeature>
                        </FlexGroup>
                        <Pagination
                            page={ page_index }
                            total={ page_count }
                            offset={ pageSize }
                            onSelect={ onSelect }
                        />
                        <div className="page-size-select">
                            <PageSizeSelect
                                onChange={ selection => onChange(selection.value) }
                            />
                        </div>
                        <TotalCount count={ total } />
                        <TotalSelection count={ selectedIdList.length } />
                    </FlexGroup>
                    <Refresh time={ moment(responseTime) } onClick={ onFetch } />
                </FlexGroup>
                <ListView
                    header={ headerConfig }
                    onSort={ onSort }
                    list={ data_list }
                    dataKey="id"
                    renderListRow={ ListRowGroup }
                    fixed={ isMobile }
                    defaultSelect={ selectedIdList }
                    selectable={ permissionHandler({ requiredList: [AUTH_EDIT_FILTER] }) }
                    onSelect={ onRowSelected }
                />
            </section>
            <RemoveFilterModel
                selectedFilters={ selectedIdList }
                show={ showRemove }
                onClose={ closeRemoveModal}
            />
        </>
    );
};

FilterManagementList.propTypes = {
    onExport: PropTypes.func,
    onChange: PropTypes.func,
    onFetch: PropTypes.func,
    onSort: PropTypes.func,
    onSelect: PropTypes.func,
    data: PropTypes.shape({
        data_list: PropTypes.arrayOf(PropTypes.shape({})),
        page_count: PropTypes.number,
        page_index: PropTypes.number,
        size: PropTypes.number,
        total: PropTypes.number,
        updatedTime: PropTypes.oneOfType([
            PropTypes.instanceOf(moment),
            PropTypes.number,
        ]),
    }),
    selectedIdList: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.string,
    ])),
    onRowSelected: PropTypes.func,
};
FilterManagementList.defaultProps = {
    onChange: () => {},
    onExport: () => {},
    onFetch: () => {},
    onSort: () => {},
    onSelect: () => {},
    data: {},
    selectedIdList: [],
    onRowSelected: () => {},
};
export default FilterManagementList;
